import {
	ArcElement,
	Chart as ChartJS,
	ChartOptions,
	Colors,
	Legend,
	Plugin,
	Tooltip,
} from 'chart.js';
import { AnyObject } from 'chart.js/dist/types/basic';
import { uniqueId } from 'lodash';
import React from 'react';
import { Pie as ReactChartPieChart } from 'react-chartjs-2';

import { afterUpdateFn, colors, FormatStyle } from './common';
import {
	container as containerStyle,
	legendContainer as legendContainerStyle,
	pieChart as pieChartStyle,
	subtitleContainer as subtitleContainerStyle,
	titleContainer as titleContainerStyle,
} from './styles.module.css';

ChartJS.register(ArcElement, Colors, Tooltip, Legend);

interface PieChartData {
	legend?: string;
	xValues?: string[];
	yValues?: number[];
}
interface PieChartProps {
	displayTitle: string;
	subtitle: string;
	dataItems?: PieChartData;
}

interface ChartOptionsWithPlugin extends ChartOptions<'pie'> {
	plugins: ChartOptions['plugins'] & {
		htmlLegend: { containerID: string; formatStyle: FormatStyle };
	};
}

// Picks up legend for other
const htmlLegendPlugin: Plugin<'pie', AnyObject> = {
	id: 'htmlLegend',
	afterUpdate: afterUpdateFn({ includeXValues: true }),
};

export const PieChart = ({ displayTitle, subtitle, dataItems }: PieChartProps) => {
	if (!dataItems) return <></>;

	const labels = dataItems.xValues ?? [];
	// No need to turn into percentages, chart.js does this automatically
	const data = dataItems.yValues ?? [];
	const legendContainerId = uniqueId('legend-container-');

	const options: ChartOptionsWithPlugin = {
		animation: false,
		responsive: true,
		// We are taking over the AR (setting height/width in CSS)
		maintainAspectRatio: false,
		plugins: {
			htmlLegend: {
				containerID: legendContainerId,
				formatStyle: 'percentage' as FormatStyle,
			},
			legend: {
				display: false,
			},
			title: {
				display: false,
			},
		},
	};

	const pieData = {
		labels,
		datasets: [
			{
				label: dataItems.legend,
				data,
				backgroundColor: colors,
				borderColor: ['#F8F8F8'],
				borderWidth: 2,
			},
		],
	};

	return (
		<div className={containerStyle}>
			<div className={titleContainerStyle}>{displayTitle}</div>
			<div id={legendContainerId} className={legendContainerStyle}>
				{/* @todo: replace IDs with refs */}
				<ul></ul>
			</div>
			<div className={pieChartStyle}>
				<ReactChartPieChart data={pieData} options={options} plugins={[htmlLegendPlugin]} />
			</div>
			<div className={subtitleContainerStyle}>{subtitle}</div>
		</div>
	);
};
